<template>
  <div id="factor-details">
	<div v-if="selectedFactor.label !== undefined">
		<div class="back-btn">
			<back-nav-btn :action="backToEvaluationFactorPool">Back To Evaluation Factor Pool</back-nav-btn>
		</div>
		<editActionBar
			:isEditActive="update"
			:updateText="'Edit ' + selectedFactor.label"
			:confirmAction="onSave" 
			:updateAction="onEditFactorDecision" 
			:cancelAction="onCancel" 
			:deleteAllowed="false"
			:disableWithoutDelete="true"
			:enableAction="onDisableEnable" 
			:disableAction="onDisableEnable" 
			:isDisabled="!selectedFactor.isActive"
		/>
		<div>
			<h3 class="line-header">Factor Definition</h3>
			<v-form id="factor-definition" class="factor-definition" :key="update" v-model="isFactorValid">
				<div class="definition-details">
					<fieldset class="factor-identification">
						<v-text-field
							v-model="selectedFactor.factorId"
							label="Factor ID"
							:readonly="true"
							class="factor-id"
						/>
						<v-text-field
							v-model="selectedFactor.label"
							label="Display Label"
							:readonly="!update"
							:outlined="update"
							:rules="rules"
							class="factor-label"
						/>
					</fieldset>
					<fieldset>
						<label for="description-text">Description</label>
						<v-textarea
							id="description-text"
							v-model="selectedFactor.description"
							no-resize
							:readonly="!update"
							:outlined="update"
							:rules="rules"
						>
						</v-textarea>

						<label for="how-it-works-text">How It Works</label>
						<v-textarea
							id="how-it-works-text"
							v-model="selectedFactor.whyItWorks"
							no-resize
							:readonly="!update"
							:outlined="update"
							:rules="rules"
						>
						</v-textarea>

						<v-text-field
							v-if="update"
							v-model="selectedFactor.knowledgeBaseUrl"
							label="Learn More Link"
							:readonly="!update"
							:outlined="update"
							:rules="rules"
							class="factor-learn-more"
						/>
						<div v-else class="learn-more-link">
							<label for="knowledge-base-url">Learn More Link</label>
							<div id="knowledge-base-url">
								<a :href="selectedFactor.knowledgeBaseUrl" target="_blank">{{ selectedFactor.knowledgeBaseUrl }}</a>
							</div>
						</div>
					</fieldset>
				</div>
			</v-form>
		</div>
		<div v-if="factorMetaDataBuilt" class="thresholds">
			<h3 class="line-header">Thresholds</h3>
			<v-form id="factor-thresholds" class="factor-thresholds" :key="update" v-model="isFactorValid">
					<v-data-table
						:headers="thresholdHeaders"
						:items="factor.thresholds"
						:expanded.sync="expanded"
						item-key="thresholdId"
						mobile-breakpoint="0"
						show-expand
					>
						<template v-slot:item.thresholdId="{ item }">
							{{ item.thresholdId.toUpperCase() }}
						</template>

						<template v-slot:expanded-item="{ headers, item }">
							<td :colspan="headers.length">
								<fieldset>
									<div class="expanded-threshold-read-only">
										<v-text-field
											v-model="item.thresholdId"
											label="Threshold ID"
											:readonly="true"
											class="threshold-id"
										/>
										<v-text-field
											v-model="item.levels"
											label="Levels"
											:readonly="true"
											class="threshold-levels"
										/>
										<v-text-field
											v-model="item.orderingType"
											label="Order"
											:readonly="true"
											class="threshold-order"
										/>
									</div>
								</fieldset>
								<fieldset>
									<div class="expanded-threshold-update">
										<v-textarea
											v-model="item.label"
											label="Display Label"
											no-resize
											:readonly="!update"
											:outlined="update"
											:class="update ? 'threshold-label-update' : 'threshold-label'"
										/>
										<v-text-field
											v-model="item.placeholder"
											label="Value Placeholder"
											:readonly="!update"
											:outlined="update"
											class="threshold-placeholder"
										/>
									</div>
								</fieldset>
							</td>
						</template>
					</v-data-table>
			</v-form>
		</div>
		<v-skeleton-loader v-else type="table" class="thresholds"></v-skeleton-loader>
	</div>
	<v-skeleton-loader v-else type="article"></v-skeleton-loader>
  </div>
</template>

<script>
	/**
	 * @fileoverview factor-details.vue displays the meta data for the selected evaluation factor.
	 * It relies on vuex to map the state variables from the store files.
	 * 
	 * @components
	 * 		editActionBar - action bar that allows the user to edit, save changes, cancel changes, enable and disable factors
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */

	import { mapState } from 'vuex';
	import editActionBar from '@/components/edit-action-bar.vue';

	export default {
		props: {
			/**
			 * Selected factor meta data
			 */
			factor: {
				required: true
			}
		},
		data() {
			return {
				isFactorValid: false,	// validation flag
				update: false,		// updating in progress
				/**
				 * Validation rules for data fields
				 */
				rules: [
					value => !!value || 'Required',
					value => (value || '').length > 0 || 'Required',
				],
				tempFactor: {},	// temporary factor object to restore changes
				expanded: [],	// list of expanded items
				singleExpand: false,	// allow multiple expanded items
				/**
				 * Headers for threshold display
				 */
				thresholdHeaders: [
					{
						text: "",
						align: "start",
						value: "thresholdId",
						sortable: false
					}
				]
			}
		},
		components: {
			editActionBar
		},
		computed: {
			...mapState({
				activeTenantId: (state) => state.account.activeTenantId,
				factorMetaDataBuilt: (state) => state.config.factorMetaDataBuilt
			}),
			/**
			 * Getter and setter for the selected evaluation factor
			 */
			selectedFactor: {
				get() {
					return this.factor;
				},
				set(factor) {
					this.selectedFactor.tenantId = this.activeTenantId;
					this.selectedFactor.category = factor.category;
					this.selectedFactor.description = factor.description;
					this.selectedFactor.factorId = factor.factorId;
					this.selectedFactor.functionName = factor.functionName;
					this.selectedFactor.isActive = factor.isActive;
					this.selectedFactor.knowledgeBaseUrl = factor.knowledgeBaseUrl;
					this.selectedFactor.label = factor.label;
					this.selectedFactor.scope = factor.scope;
					this.selectedFactor.thresholds = factor.thresholds;
					this.selectedFactor.treeMode = factor.treeMode;
					this.selectedFactor.type = factor.type;
					this.selectedFactor.whyItWorks = factor.whyItWorks;
				}
			}
		},
		watch: {
			/**
			 * If the factor meta data is completely built, expand the thresholds
			 */
			factorMetaDataBuilt() {
				if (this.factorMetaDataBuilt) {
					this.expanded = this.selectedFactor.thresholds;
				}
			}
		},
		/**
		 * Mounted lifecycle hook
		 * If the factor meta data is completely built, expand the thresholds
		 */
		mounted() {
			if (this.factorMetaDataBuilt) {
				this.expanded = this.selectedFactor.thresholds;
			}
		},
		methods: {
			/**
			 * Return to the evaluation factor pool
			 */
			backToEvaluationFactorPool() {
				this.$emit("backToEvaluationFactorPool");
			},
			/**
			 * Save the factor meta data. Don't send the levels and ordering type to the API
			 */
			onSave(){
				this.update = false;

				if (this.isFactorValid) {
					this.selectedFactor.tenantId = this.activeTenantId;
					
					// create a new payload with everything except the levels and orderingType
					let thresholds = [];
					this.selectedFactor.thresholds.map(threshold => {
						let tempThreshold = {
							label: threshold.label,
							placeholder: threshold.placeholder,
							thresholdId: threshold.thresholdId,
							validateRegex: threshold.validateRegex
						}

						thresholds.push(tempThreshold);
					});

					let updateFactor = {
						tenantId: this.selectedFactor.tenantId,
						category: this.selectedFactor.category,
						description: this.selectedFactor.description,
						factorId: this.selectedFactor.factorId,
						functionName: this.selectedFactor.functionName,
						isActive: this.selectedFactor.isActive,
						knowledgeBaseUrl: this.selectedFactor.knowledgeBaseUrl,
						label: this.selectedFactor.label,
						scope: this.selectedFactor.scope,
						thresholds: thresholds,
						treeMode: this.selectedFactor.treeMode,
						type: this.selectedFactor.type,
						whyItWorks: this.selectedFactor.whyItWorks,
					};

					this.$store.dispatch("updateFactorMetaData", updateFactor);
				} else {
					this.$store.dispatch("notifications/addNotification", {
						status: 'error',
						message: 'Fix missing/invalid information and try again.',
					});
				}
			},
			/**
			 * Cancel the changes and restore the old data
			 */
			onCancel() {
				this.update = false;
				
				// restore selectedFactor from backup... then cleanup
				this.selectedFactor = this.tempFactor;
				this.tempFactor = {};
			},
			/**
			 * Edit the factor meta data. Create a temporary factor for restoration
			 */
			onEditFactorDecision() {
				this.update = true;

				// make deep clone of factor for restoration
				this.tempFactor = this._.cloneDeep(this.factor);
			},
			/**
			 * Disable or Enable the factor. Send everything EXCEPT the levels and orderingType to the API
			 */
			onDisableEnable() {
				this.selectedFactor.tenantId = this.activeTenantId;
				this.selectedFactor.isActive = !this.selectedFactor.isActive;

				// create a new payload with everything except the levels and orderingType
				let thresholds = [];
				this.selectedFactor.thresholds.map(threshold => {
					let tempThreshold = {
						label: threshold.label,
						placeholder: threshold.placeholder,
						thresholdId: threshold.thresholdId,
						validateRegex: threshold.validateRegex
					}

					thresholds.push(tempThreshold);
				});

				let updateFactor = {
					tenantId: this.selectedFactor.tenantId,
					category: this.selectedFactor.category,
					description: this.selectedFactor.description,
					factorId: this.selectedFactor.factorId,
					functionName: this.selectedFactor.functionName,
					isActive: this.selectedFactor.isActive,
					knowledgeBaseUrl: this.selectedFactor.knowledgeBaseUrl,
					label: this.selectedFactor.label,
					scope: this.selectedFactor.scope,
					thresholds: thresholds,
					treeMode: this.selectedFactor.treeMode,
					type: this.selectedFactor.type,
					whyItWorks: this.selectedFactor.whyItWorks,
				};

				this.$store.dispatch("updateFactorMetaData", updateFactor);
			}
		}
	}
</script>

<style>

</style>